/** English strings. Keys are shared with ./ko.js — keep the two in step. */
const en = {
  common: {
    cancel: 'Cancel',
    close: 'Close',
    remove: 'Remove',
    retry: 'Reset',
    loading: 'Loading…',
    refresh: 'Refresh',
    language: 'Language',
    back: 'Back',
    save: 'Save',
    next: 'Next',
  },
  nav: {
    primary: 'Main',
    home: 'LabelFlow home',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    management: 'Management',
    aiAssistant: 'AI Assistant',
    projectManagement: 'Project Management',
    modelManagement: 'Model Management',
    datasetPrep: 'Dataset Prep',
    videoToImages: 'Video to Images',
    publicHub: 'Public Hub',
    modelHub: 'Model Hub',
    inference: 'Inference',
    prediction: 'Prediction',
    account: 'Account',
    logout: 'Logout',
    logoutConfirm: 'Are you sure you want to logout?',
    themeLight: 'Switch to light mode',
    themeDark: 'Switch to dark mode',
  },
  videoToImages: {
    title: 'Video to Images',
    subtitle:
      'Extract JPEG frames from videos in the background. Send them straight into a model, or hold them in a dataset and transfer them later.',
    engine: {
      label: 'Extraction engine',
      ffmpeg: 'ffmpeg',
      opencv: 'OpenCV',
      unknown: 'checking…',
      hint: 'Chosen automatically — ffmpeg when available, otherwise OpenCV.',
    },

    target: {
      heading: '1. Choose where the images go',
      mode: 'Destination',
      modeModel: 'A project model',
      modeDataset: 'A dataset',
      project: 'Project',
      model: 'Model',
      selectProject: 'Select a project',
      selectModel: 'Select a model',
      noModels: 'This project has no models yet',
      hint: 'A new dataset version is created under the selected model. The extracted images arrive unlabeled and open straight in the labeling tool.',
      datasetName: 'Dataset name',
      datasetPlaceholder: 'e.g. Warehouse cam — March',
      datasetHint: 'No project or model needed. The images are held in this dataset, and you can transfer any selection of them into a project model later.',
      datasetExistsHint: 'Using a name that already exists adds the new images to that dataset.',
      newProject: '+ New project',
      newModel: '+ New model',
      newProjectName: 'New project name',
      newModelName: 'New model name',
      newProjectPlaceholder: 'e.g. Warehouse safety',
      newModelPlaceholder: 'e.g. Helmet detector',
      modelKind: 'Model type',
      kindClassification: 'Classification',
      kindDetection: 'Object detection',
      create: 'Create',
      creating: 'Creating…',
      created: '“{name}” created.',
      createFailed: 'Could not create it',
      cancelCreate: 'Cancel',
      nameRequired: 'Give it a name first.',
      missing: 'Select a project and a model first.',
      datasetMissing: 'Give the dataset a name first.',
    },

    upload: {
      heading: '2. Add videos',
      dropHere: 'Drop videos, a folder, or a .zip here',
      or: 'or',
      chooseFiles: 'Choose files',
      chooseFolder: 'Choose folder',
      supported: 'Supported: {formats}',
      zipNote: 'Zip archives are searched recursively; non-video entries are ignored.',
      selectedCount: '{count} file(s) selected',
      clearAll: 'Clear all',
      uploading: 'Uploading {name}…',
      uploadProgress: '{percent}% of {size}',
      staged: '{count} video(s) ready',
      rejected: '{count} file(s) skipped',
      rejectedHeading: 'Skipped files',
      uploadFailed: 'Upload failed',
      noneSelected: 'Add at least one video first.',
      totalSize: 'Total {size}',
    },

    options: {
      heading: '3. Extraction options',
      sampling: 'Frame sampling',
      samplingFps: 'Fixed frame rate',
      samplingNth: 'Every Nth frame',
      samplingCount: 'Fixed total count',
      fpsLabel: 'Frames per second',
      fpsHint: '1 fps takes one frame every second of video.',
      nthLabel: 'Keep every Nth frame',
      nthHint: 'N = 30 keeps roughly 1 frame per second of 30 fps footage.',
      countLabel: 'Total frames per video',
      countHint: 'Frames are sampled evenly across the whole video.',
      format: 'Output format',
      quality: 'JPEG quality',
      qualityHint: 'Higher keeps more detail and makes bigger files.',
      resize: 'Resize',
      resizeKeep: 'Keep original size',
      resizeMax: 'Fit within a maximum',
      maxWidth: 'Max width',
      maxHeight: 'Max height',
      resizeHint: 'Images are only ever scaled down, and aspect ratio is preserved.',
    },

    start: {
      button: 'Start conversion',
      starting: 'Starting…',
      queued: 'Conversion queued — you can leave this page.',
      failed: 'Could not start the conversion',
    },

    jobs: {
      heading: 'Conversion jobs',
      empty: 'No conversion jobs yet.',
      emptyHint: 'Add videos above and start a conversion — it will appear here.',
      loadFailed: 'Could not load conversion jobs.',
      columnTarget: 'Target',
      columnStatus: 'Status',
      columnProgress: 'Progress',
      columnImages: 'Images',
      columnCreated: 'Created',
      columnActions: '',
      sources: '{done}/{total} video(s)',
      frames: '{count} image(s)',
      framesOf: '{count} of ~{total} image(s)',
      version: 'Version {version}',
      datasetTarget: 'Dataset',
      openInLabeling: 'Label',
      aiLabel: 'AI label',
      aiLabelTooltip: 'Let the AI group or box the unlabeled images in this version',
      openInLabelingTooltip: 'Open in the labeling tool',
      openDataset: 'Dataset',
      openDatasetTooltip: 'Open this dataset',
      viewDetails: 'Details',
      viewDetailsTooltip: 'Job details, per-file results and log',
      cancelJob: 'Cancel',
      cancelJobTooltip: 'Cancel this conversion',
      cancelConfirmTitle: 'Cancel this conversion?',
      cancelConfirmText: 'Images already extracted are kept and stay usable for labeling.',
      cancelled: 'Cancellation requested.',
      cancelFailed: 'Could not cancel the job.',
      background: 'Conversions keep running after you leave this page.',
    },

    status: {
      queued: 'Queued',
      inProgress: 'Running',
      complete: 'Completed',
      partiallyComplete: 'Partially completed',
      failed: 'Failed',
      cancelled: 'Cancelled',
      unknown: 'Unknown',
    },

    details: {
      heading: 'Job details',
      output: 'Output location',
      engineUsed: 'Engine',
      optionsUsed: 'Options',
      perFile: 'Per-file results',
      fileName: 'File',
      fileStatus: 'Status',
      fileFrames: 'Images',
      fileError: 'Reason',
      warnings: 'Warnings',
      errors: 'Error',
      skipped: 'Skipped on upload',
      logs: 'Recent log',
      noLogs: 'No log lines yet.',
      totalProduced: '{count} image(s) produced',
      noneProduced: 'No images were produced.',
    },

    datasets: {
      heading: 'Datasets',
      subtitle: 'Extracted images waiting to be assigned to a model.',
      empty: 'No datasets yet.',
      emptyHint: 'Run a conversion with a dataset name and it will show up here.',
      loadFailed: 'Could not load datasets.',
      images: '{count} image(s)',
      open: 'Open',
      backToList: 'All datasets',
      selectAll: 'Select all',
      clearSelection: 'Clear',
      selected: '{count} selected',
      selectHint: 'Select images, then transfer them into any project model.',
      transfer: 'Prepare for labeling',
      removeSelected: 'Remove',
      removeConfirmTitle: 'Remove the selected images?',
      removeConfirmText: 'They are removed from this dataset. Copies already transferred into a model are not affected.',
      removed: '{count} image(s) removed.',
      removeFailed: 'Could not remove the images.',
      noneSelected: 'Select at least one image.',
      alreadySent: 'Already sent to {count} model(s)',
      loadMore: 'Load more',
      showing: 'Showing {shown} of {total}',
      deleteDataset: 'Delete dataset',
      deleteDatasetConfirmTitle: 'Delete this dataset?',
      deleteDatasetConfirmText: 'The dataset and its images are removed. Images already transferred into a model are not affected.',
      datasetDeleted: 'Dataset deleted.',
      sourceVideo: 'From {name}',
      download: 'Download',
      downloadAll: 'Download all',
      downloadTooltip: 'Download these images as a zip',
      downloadAllTooltip: 'Download the whole dataset as a zip',
      downloading: 'Preparing the download…',
      downloaded: '{count} image(s) downloaded.',
      downloadFailed: 'Could not build the download',
    },

    build: {
      heading: 'Prepare these images for labeling',
      intro: '{count} image(s) from “{dataset}”.',
      taskHeading: '1. What should these images become?',
      taskClassification: 'Classification',
      taskClassificationHint: 'The AI groups similar frames, and each group becomes a class.',
      taskDetection: 'Object detection',
      taskDetectionHint: 'The AI draws a box around each object you name.',
      taskUnlabeled: 'Just add them',
      taskUnlabeledHint: 'No AI. The images arrive unlabeled and you label them yourself.',
      classesHeading: 'How many classes?',
      classesAuto: 'Let the AI decide',
      classesFixed: 'Exactly this many',
      classCount: 'Classes',
      classNames: 'Class names (optional)',
      classNamesPlaceholder: 'e.g. empty, loading, blocked',
      classNamesHint: 'Comma separated. Leave blank and the groups are named for you.',
      objectsHeading: 'What should it look for?',
      objectsPlaceholder: 'e.g. person, forklift, helmet',
      objectsHint: 'Comma separated, in plain words. Each one becomes a class.',
      objectsRequired: 'Name at least one object to look for.',
      confidence: 'Confidence',
      confidenceHint: 'Higher finds fewer but surer objects.',
      destinationHeading: '2. Where should it go?',
      appendNote: 'The images are ADDED to the chosen version. Nothing already in it is removed or replaced, and names that would clash are given a suffix.',
      start: 'Start',
      starting: 'Starting…',
      queued: 'Started — it keeps running if you leave this page.',
      failed: 'Could not start',
      analysing: 'Looking at your images…',
      creating: 'Creating the project and model…',
      writing: 'Adding the images…',
      progressOf: '{done} of {total}',
      runningHeading: 'Preparing images',
      resultHeading: 'Ready to label',
      resultLine: '{imported} image(s) in {model} version {version} — {labeled} labeled.',
      resultClasses: 'Classes: {names}',
      openLabeling: 'Open in labeling',
      dismiss: 'Dismiss',
      cancelBuild: 'Stop',
      cancelConfirmTitle: 'Stop preparing?',
      cancelConfirmText: 'Anything it has already created is removed, so nothing half-made is left behind.',
      cancelled: 'Stopped.',
      buildFailed: 'Preparation failed',
      versionHeading: 'Label the images already in this model',
      versionIntro: '{count} unlabeled image(s) in {model} version {version}.',
      versionNothing: 'Every image in this version is already labeled.',
      versionNote: 'The images stay where they are — only the unlabeled ones get a label, and anything you labeled by hand is left alone.',
      versionTarget: 'Destination',
    },
    transfer: {
      heading: 'Transfer to a project model',
      intro: 'Adding {count} image(s) from “{dataset}”.',
      project: 'Project',
      model: 'Model',
      version: 'Target version',
      selectProject: 'Select a project',
      selectModel: 'Select a model',
      noModels: 'This project has no models yet',
      versionLatest: 'Version {version} — {count} image(s) now',
      versionNew: 'Create a new version',
      appendNote: 'The images are ADDED to the chosen version. Nothing already in it is removed or replaced, and names that would clash are given a suffix.',
      removeFromDataset: 'Also remove them from this dataset',
      confirm: 'Transfer {count} image(s)',
      transferring: 'Transferring…',
      success: '{count} image(s) added to {model} version {version} — it now holds {total}.',
      partial: '{count} image(s) transferred, {failed} could not be copied.',
      failed: 'Transfer failed',
      selectModelFirst: 'Choose a project and model first.',
    },
  },
  aiLabel: {
    preLabel: {
      title: 'Your trained model',
      hint: "Draft this version's labels with a version you already trained, then correct them.",
      version: 'Trained version',
      confidence: 'Confidence',
      current: 'This image',
      currentTip: 'Pre-label the image on screen',
      unlabeled: '{count} unlabeled',
      unlabeledTip: 'Pre-label every image that has no labels yet',
      running: 'Running…',
      progress: '{done} / {total} · {found} found',
      stop: 'Stop',
      stopping: 'Stopping…',
      stopTip: 'Stop pre-labeling',
      noDetections: 'This model found nothing above the confidence threshold — try lowering it.',
      failed: 'Pre-labeling failed: {reason}',
    },
    panel: {
      title: 'AI Auto Label',
      close: 'Close AI panel',
    },
    mode: {
      label: 'Detection mode',
      myclasses: {
        noClasses: 'You have no classes yet, so this falls back to Smart mode — it will detect what it can and create classes for what it finds.',
        label: 'My classes only (recommended)',
        hint: 'Detects ONLY the classes you defined and boxes them — no new classes are created. Set “AI detect as” on a class to map it to what the AI should look for.',
      },
      smart: {
        label: 'Smart (detect everything)',
        hint: 'YOLO + SAM + CLIP — finds objects even outside your classes and adds new ones',
      },
      coco: {
        label: 'COCO standard (80 classes)',
        hint: 'Fast, common objects only',
      },
    },
    confidence: {
      label: 'Confidence',
      hint: 'Higher = fewer, surer boxes. Lower = more boxes. Applies to the next run and re-filters the suggestions already on this image.',
    },
    iou: {
      label: 'Overlap (IoU)',
      hint: 'How much two boxes may overlap before one is treated as a duplicate.',
    },
    nav: {
      autoLabel: 'Auto-label images as I open them',
    },
    master: {
      use: 'Use a master image',
      hint: 'Draw boxes on one reference image, then the AI labels only those object types across the rest.',
      set: 'Set current image as master',
      current: 'Master: {name}',
      boxes: '{boxes} box(es) · {classes} class(es)',
      noBoxes: 'Draw boxes on the master image and give each one a class, then run the auto-label below.',
      clear: 'Clear master',
      goto: 'Open master image',
      run: 'Auto-label from master ({count})',
      runTip: 'Label the remaining images with the master’s classes only',
      similarity: 'Appearance match',
      similarityHint: 'How closely a detection must look like the boxes you drew. 0 = off (restrict by class only).',
      missingTitle: 'No master image yet',
      missingText: 'Pick a master image and draw at least one box with a class on it first.',
      emptyTitle: 'Master has no boxes',
      emptyText: 'Draw at least one box on the master image and assign a class to it.',
      aloneTitle: 'Nothing else to label',
      aloneText: 'The master image is the only image without labels.',
      unclassedTitle: 'Some master boxes have no class',
      unclassedText: '{count} box(es) on the master image have no class and will be ignored.',
      marker: 'Master image for AI auto-label',
      badge: 'MASTER',
    },
    action: {
      detectThis: 'Detect this image',
      detectThisTip: 'Detect objects in the current image',
      detecting: 'Detecting…',
      detectAll: 'Auto-label all unlabeled ({count})',
      detectAllTip: 'Auto-label every image without labels',
      stop: 'Stop',
      stopTip: 'Stop auto-labeling — boxes already created are kept',
      stopping: 'Stopping…',
    },
    progress: {
      counts: '{done}/{total} images · {found} boxes',
    },
    review: {
      suggestions: '{count} AI suggestion(s)',
      accept: 'Accept all',
      acceptTip: 'Keep all AI boxes on this image',
      discard: 'Discard',
      discardTip: 'Remove all AI boxes on this image',
      hint: 'Dashed boxes are suggestions — move/resize/delete them like normal, or change class from the right panel.',
    },
    result: {
      stopped: 'Auto-label stopped',
      complete: 'Auto-label complete',
      summary: 'Labeled <b>{done}</b> image(s) with <b>{found}</b> AI boxes.',
      newClasses: '<b>{count}</b> new class(es) created.',
      failed: '{count} image(s) failed.',
      reviewNote: 'Suggestions are dashed — review, edit, then Accept.',
      navOff: 'Auto-label-on-open was switched off so nothing restarts.',
      noObjects: 'No new objects found',
      allLabeled: 'All images already have labels',
      detectFailed: 'Auto-detect failed',
      unknownError: 'Unknown error',
      gateOffTitle: 'Appearance matching was unavailable',
      gateOff: 'Appearance matching was OFF for this run — detections were restricted to your master classes by name only, not by how they look. Reason: {reason}',
      notFoundClasses: 'Not found in any image: <b>{list}</b>. Try lowering the confidence or the appearance match, or add an “AI detect as” alias to those classes.',
    },
    upload: {
      title: 'AI Auto Label?',
      body: '<b>{count}</b> new image(s) uploaded.<br/>Detect all objects automatically?',
      confirm: 'Auto-label now',
      cancel: 'Not now',
    },
  },

  autoGroup: {
    emptyTitle: 'No classes yet',
    emptyWithAi: 'Let the AI find the groups, or set a count on the right and hit Create Classes.',
    emptyManual: 'Set a count on the right and hit Create Classes to start.',
    title: 'Group these for me',
    open: 'AI group',
    lead: 'I will look at {count} unlabeled image(s) and sort them into groups by how they look.',
    capped: 'You have {total}. I will group {count} of them; the other {rest} stay unlabeled.',
    notEnough: 'Add at least {min} unlabeled images first — grouping needs something to compare.',
    howMany: 'How many groups?',
    auto: 'Find them for me',
    run: 'Group them',
    stop: 'Stop',
    reading: 'Reading images… {done} of {total}',
    grouping: 'Looking for groups…',
    result: 'Sorted {grouped} image(s) into {groups} group(s). Rename them on the right.',
    resultRest: '{count} image(s) were left unlabeled.',
    footnote: 'Nothing is saved until you press Save. Drag any image to a different class to correct it.',
  },
  assistant: {
    title: 'AI Assistant',
    welcome: {
      title: 'What are you building?',
      titleNamed: 'What are you building, {name}?',
      subtitle:
        'Ask about model choice, dataset size, labeling strategy or training results. I can read your projects and models, so answers can point at your real data.',
      contextLine: 'Reading {projects} project(s), {models} model(s), {images} images',
    },
    badge: {
      ready: 'Ready',
      limited: 'Limited mode',
      grounded: 'Your data',
      stopped: 'Stopped',
      yourData: 'Your data',
    },
    modelType: {
      classification: 'Classification',
      detection: 'Object detection',
      segmentation: 'Segmentation',
      unknown: 'Unknown type',
    },
    trainingStatus: {
      pending: 'Not trained',
      queued: 'Queued',
      inprogress: 'Training',
      completed: 'Trained',
      failed: 'Failed',
    },
    quick: {
      heading: 'Go to',
      projects: 'Projects',
      models: 'Models',
      video: 'Video to Images',
      hub: 'Model Hub',
      predict: 'Prediction',
    },
    suggest: {
      compare: {
        label: 'Classification or detection?',
        prompt:
          'I need to decide between image classification and object detection. Explain the difference in practical terms and help me pick one for a real inspection use case.',
      },
      datasetSize: {
        label: 'How much data do I need?',
        prompt:
          'How many labeled images per class do I need before training is worth running, and what happens if I have fewer?',
      },
      split: {
        label: 'Train / val / test split',
        prompt:
          'How should I split my dataset into train, validation and test sets, and why does the split matter?',
      },
      classes: {
        label: 'Designing my classes',
        prompt:
          'How should I choose and name the classes in my dataset? Is it better to have a few broad classes or many specific ones?',
      },
      video: {
        label: 'From video to dataset',
        prompt:
          'I have video footage rather than still images. What is the best way to turn it into a labeled training dataset?',
      },
      metrics: {
        label: 'Reading my metrics',
        prompt:
          'Explain mAP50, mAP50-95, precision and recall, and tell me which one to look at first when a model underperforms.',
      },
      firstProject: {
        label: 'Start my first project',
        prompt:
          'I am new to LabelFlow. Walk me through the steps from an empty account to a trained model.',
      },
      pickType: {
        label: 'Which model type fits?',
        prompt:
          'Help me pick the right model type for my use case. Ask me whatever you need to know about it first.',
      },
      ctxReview: {
        label: 'Review my training run',
        prompt:
          'Review the training results for my model "{model}" version {version}. What do the metrics say, and what should I change to improve it?',
      },
      ctxUnlabeled: {
        label: '{count} images still unlabeled',
        prompt:
          'My model "{model}" has {count} unlabeled images out of {total}. What is the most efficient way to get them labeled and the model trained?',
      },
      ctxThin: {
        label: 'Is my dataset big enough?',
        prompt:
          'My model "{model}" has {labeled} labeled images across {classes} classes. Is that enough to train on, and what should I do if it is not?',
      },
      ctxReady: {
        label: 'Ready to train — now what?',
        prompt:
          'My model "{model}" has all {count} images labeled ({type}). Should I train it now, and which settings should I use?',
      },
    },
    sidebar: {
      heading: 'Conversations',
      newChat: 'New chat',
      searchPlaceholder: 'Search conversations',
      today: 'Today',
      yesterday: 'Yesterday',
      week: 'Previous 7 days',
      older: 'Older',
      untitled: 'Untitled chat',
      empty: 'No messages yet',
      count: '{count} message(s)',
      noThreads: 'No conversations yet. Ask a question to start one.',
      noMatches: 'No conversation matches that search.',
      rename: 'Rename',
      delete: 'Delete',
      deleteTitle: 'Delete conversation',
      deleteBody: 'Delete "{title}"? This cannot be undone.',
      clearAll: 'Delete all conversations',
      clearTitle: 'Delete all conversations',
      clearBody: 'Every saved conversation will be removed. This cannot be undone.',
    },
    topbar: {
      collapseSidebar: 'Hide conversations',
      expandSidebar: 'Show conversations',
      workspace: 'Workspace the assistant can see',
      more: 'More actions',
      export: 'Export as Markdown',
      copyAll: 'Copy whole conversation',
    },
    thread: {
      jump: 'Jump to latest',
    },
    message: {
      yours: 'Your message',
      reply: 'Assistant reply',
      thinking: 'Thinking…',
      empty: 'No answer was returned. Try asking again.',
      stopped: 'Stopped before any text arrived.',
      copy: 'Copy',
      copied: 'Copied',
      edit: 'Edit',
      editLabel: 'Edit your message',
      saveAndResend: 'Save & resend',
      regenerate: 'Regenerate',
      retry: 'Retry',
    },
    composer: {
      placeholder: 'Ask about model choice, dataset size, or your training results…',
      label: 'Ask the AI assistant a question',
      send: 'Send',
      stop: 'Stop generating',
      hint: 'Enter to send · Shift+Enter for a new line',
      contextOn: 'Using your workspace',
      contextOff: 'Workspace off',
      contextHint:
        'When on, your projects, models and labeling counts are sent with the question so answers can reference them.',
      contextUnavailable: 'No workspace data is available to send.',
    },
    context: {
      heading: 'Your workspace',
      useLabel: 'Ground answers in my workspace',
      useHint:
        'Sends a summary of your projects, models and image counts with each question.',
      projects: 'Projects',
      models: 'Models',
      images: 'Images',
      unlabeled: 'Unlabeled',
      none: 'Nothing here yet. Create a project and a model, and this is where the assistant will read them from.',
      rowMeta: '{total} images · {labeled} labeled · {classes} class(es)',
      rowUnlabeled: '{count} still unlabeled',
      ask: 'Ask about this',
      askPrompt:
        'Tell me about my model "{model}" version {version} — what state is it in, and what should I do next with it?',
    },
    shortcuts: {
      title: 'Keyboard shortcuts',
      send: 'Send the message',
      newline: 'New line',
      newChat: 'Start a new chat',
      editLast: 'Edit your last message (empty box)',
      stop: 'Stop generating',
      focus: 'Focus the message box',
      help: 'Show this list',
    },
    export: {
      copiedTitle: 'Copied',
      copiedBody: 'The conversation is on your clipboard as Markdown.',
      failTitle: 'Could not complete that',
      failBody: 'Your browser blocked the download or clipboard access.',
    },
    interview: {
      title: 'Let me set this up',
      reading: 'Reading your archive…',
      facts: '{images} usable image(s) · {size}',
      willImport: 'I can import {count} of them in one go.',
      droppedCount: '{count} will not fit in a single import.',
      droppedBytes: '{count} more exceed the size limit for one upload.',
      ask: {
        task: 'What would you like from these images?',
        grouping: 'How should I group them?',
        objects: 'Which objects should I look for?',
        objectsHint: 'Name them the way you would say them out loud — "helmet, safety vest, person".',
        objectsCost: 'I will look at {images} image(s) — about {seconds}s on a GPU box, longer without one.',
      },
      task: {
        classification: 'Sort them into groups',
        detection: 'Find objects in them',
        manual: 'Just import them, I will label by hand',
      },
      grouping: {
        auto: 'Find the groups for me',
        few: 'A few groups (up to 4)',
        many: 'More groups (up to 8)',
        named: 'I will name them',
        folders: 'The folders are the classes',
        explicit: '{count} group(s)',
        unlabeled: 'Imported unlabeled',
      },
      summary: {
        task: 'Goal',
        grouping: 'Grouping',
        objects: 'Looking for',
        fromArchive: 'from the archive',
        fromYou: 'you said',
      },
      change: 'Change',
      back: 'Back',
      notUnderstood: 'I read that as "{text}", which does not fit this question. Pick one below, or say it another way.',
      segmentUnsupported:
        'I cannot draw masks automatically yet — nothing in LabelFlow generates them. I can import these images into a segmentation model so you can trace them by hand, or find objects with boxes instead.',
      status: {
        starting: 'Starting…',
        step: '{step}…',
        grouping: 'Grouping {count} image(s) by how they look…',
        boxing: 'Looking for {names}…',
        verifying: '{done} of {total} images have arrived',
        elapsed: '{seconds}s',
        busyHint: 'You can change this once the current step finishes, or stop it.',
      },
      working: {
        cluster: 'Grouping {count} image(s) by how they look…',
        autobox: 'Looking for {names} — {done} of {total}',
        stop: 'Stop',
        stopRun: 'Stop the import',
        stopKeep: 'Stop and use what I have',
      },
      review: {
        failedImages: 'The detector could not be reached for {count} image(s) — that is a server problem, not your words.',
        title: 'Here is what I found',
        coverage: 'Boxes on {found} of {total} image(s)',
        lowCoverage:
          'That found very little. Those words may not match what is in the images — try different ones.',
        threshold: 'Confidence',
        retry: 'Try different words',
        groupCount: '{count} image(s)',
        rename: 'Rename',
        grouped: 'Grouped {count} image(s) into {groups} group(s)',
        stopped: 'You stopped this early, so it covers only part of the set.',
      },
      confirm: {
        title: 'Ready when you are',
        newProject: 'New project "{project}"',
        existingProject: 'Adding to your existing project "{project}"',
        modelLine: 'New {type} model "{model}"',
        imagesLine: '{count} image(s) imported',
        droppedLine: '{count} left out',
        classesLine: '{count} class(es): {names}',
        warnRepeat: 'Running this again creates a second project.',
        projectsUnreadable: 'I could not read your projects, so I will create a new one.',
        go: 'Create it and import {count} image(s)',
        cancel: 'Not yet',
        editProject: 'Project name',
        editModel: 'Model name',
        pickProject: 'Put it somewhere else',
      },
      finish: {
        openStudio: 'Open the labeling studio',
        queueTraining: 'Queue training',
        queued: 'Training is queued. It starts when a GPU frees up.',
        failed: 'Nothing was imported — the import did not finish.',
        blockedNoLabels: 'Nothing is labeled yet, so there is nothing to train on.',
        blockedOneClass: 'Training needs at least two classes.',
        blockedTooFew: 'Only {count} labeled image(s) per class — train with at least 10.',
        blockedPartial: 'Some images did not arrive, so training would use an incomplete set.',
      },
    },
    attach: {
      button: 'Attach a dataset',
      hint: 'Attach a .zip of images',
      replace: 'Replace',
      remove: 'Remove attachment',
      dropTitle: 'Drop your dataset here',
      dropBody: 'A .zip of images. Folders inside it become classes.',
      reading: 'Reading {name}…',
      phaseUnpacking: 'Unpacking the archive…',
      phaseScanning: 'Scanning {count} file(s)…',
      phaseLabels: 'Found {count} image(s) — reading labels…',
      folderButton: 'Attach a folder',
      failed: 'That archive could not be read.',
      notZip: 'Attach a .zip file.',
      tooLarge: 'That file is {size}, which is over the {limit} limit.',
      empty: 'No usable images were found in that archive.',
      summary: '{images} image(s) · {size}',
      folders: '{count} folder(s)',
      skipped: '{count} file(s) skipped',
      layoutFolders: 'folder-per-class',
      layoutFlat: 'unsorted',
      layoutYolo: 'YOLO boxes',
      layoutCoco: 'COCO boxes',
      unsupportedVoc: 'Pascal VOC boxes are not imported yet — the images will come in unlabeled.',
      classPreview: 'Classes: {names}',
      context: '[Attached {name}: {images} image(s), {folders} folder(s), layout: {layout}]',
      seed: {
        foldersLabel: 'Use the folders as classes',
        foldersPrompt: 'Create a model from this dataset, using the folders as classes.',
        splitLabel: 'Divide into 3 classes',
        splitPrompt: 'Divide these images into 3 classes and build a model from them.',
        unlabeledLabel: 'Import unlabeled',
        unlabeledPrompt: 'Import these images unlabeled so I can label them myself.',
        askLabel: 'What is in here?',
        askPrompt: 'What is in this archive, and what would you do with it?',
      },
      expired: 'The file is no longer held in this tab. Attach it again to run this.',
    },
    build: {
      title: 'Build from your dataset',
      subtitle: 'Nothing is created until you choose Create.',
      project: 'Project',
      model: 'Model',
      classesLabel: 'Classes',
      imagesLabel: 'Images',
      groupingLabel: 'Grouping',
      run: 'Create it',
      running: 'Working…',
      cancel: 'Cancel',
      retry: 'Try again',
      dismiss: 'Dismiss',
      editNames: 'Edit names',
      namePlaceholder: 'Class name',
      mode: {
        folders: 'Folders as classes',
        cluster: 'Group by similarity',
        unlabeled: 'Import unlabeled',
        annotations: 'Boxes from the archive',
      },
      modeHint: {
        folders: 'Each folder in the archive becomes one class.',
        cluster: 'Images are grouped by how they look, then you name the groups.',
        unlabeled: 'Everything imports unlabeled, ready to label in the studio.',
        annotations: 'The archive already contains boxes; they are imported as they are.',
      },
      step: {
        cluster: 'Group the images',
        project: 'Create the project',
        model: 'Create the model',
        upload: 'Upload the images',
        verify: 'Confirm they arrived',
      },
      state: {
        pending: 'Waiting',
        running: 'Working',
        done: 'Done',
        skipped: 'Not needed',
        failed: 'Failed',
      },
      note: {
        projectReused: 'Using your existing project "{name}".',
        projectRenamed: 'That project name was taken, so this one is "{name}".',
        modelRenamed: 'That model name was taken, so this one is "{name}".',
        clusterSampled: 'Grouping used {sampled} of {total} images; the rest import unlabeled.',
        capped: '{dropped} image(s) will not be imported — one import is limited to {limit}.',
        duplicates: '{count} file name(s) repeat inside a class and will be numbered apart.',
        splitFolders: 'train/ and val/ folders were read as one set of classes.',
        boxesFound: 'Found {boxes} box(es) across {images} image(s).',
        inventedClasses: '{count} class name(s) were missing from the archive and have been numbered.',
        segmentFiles: '{count} label file(s) hold polygons, not boxes, and were skipped.',
        missingSize: 'Could not read the pixel size of {count} image(s); their boxes may be off.',
      },
      result: {
        done: 'Imported {imported} image(s) into {model}.',
        partial: 'Only {imported} of {expected} image(s) arrived. The rest may still be uploading.',
        failed: 'Nothing was imported: {reason}',
        cancelled: 'Stopped. Anything already created is listed above.',
        openProject: 'Open projects',
        openModel: 'Open models',
        openLabeling: 'Start labeling',
      },
      mirror: {
        folders: 'Build **{model}** in project **{project}** from {images} image(s) across {classes} folder(s).',
        cluster: 'Build **{model}** in project **{project}** from {images} image(s), grouped into {classes} class(es).',
        unlabeled: 'Build **{model}** in project **{project}** from {images} image(s), imported unlabeled.',
        annotations: 'Build **{model}** in project **{project}** from {images} image(s) already labeled with {classes} class(es).',
        classes: 'Classes: {names}',
        capped: 'Capped: {dropped} of {total} image(s) will not be imported.',
        done: 'Created project **{project}** and model **{model}** with {imported} image(s).',
        partial: 'Created **{model}**, but only {imported} of {expected} image(s) arrived.',
        failed: 'The import did not complete: {reason}',
        cancelled: 'The import was stopped.',
      },
      guard: {
        title: 'This conversation created things',
        body: 'A dataset import above already created a project and a model. Rewriting the conversation will not undo them. Continue?',
        confirm: 'Continue',
      },
      error: {
        signedOut: 'Sign in again before importing a dataset.',
        noPlan: 'There is nothing to build from this archive.',
      },
    },
    error: {
      generic: 'Something went wrong reaching the assistant. Please try again.',
    },
  },
  prediction: {
    title: 'Prediction',
    subtitle: 'Run a model you have trained against an image it has never seen.',
    type: {
      classification: 'Classification',
      detection: 'Detection',
      segmentation: 'Segmentation',
      unknown: 'Model',
    },
    models: {
      title: 'Trained models',
      loading: 'Loading your models…',
      searchPlaceholder: 'Search models or classes',
      classCount: '{count} classes',
      unavailable: 'Unavailable',
      noOwner: 'You are signed out',
      noOwnerHint: 'Sign in again to see the models you have trained.',
      error: 'Could not load your models',
      noModels: 'No models yet',
      noModelsHint: 'Create a project and a model, label some images, then train.',
      noneTrained: 'Nothing has finished training',
      noneTrainedHint: 'A version can only be used for prediction once its training run completes.',
      noMatch: 'No model matches that',
      noMatchHint: 'Try a class name, or clear the search.',
    },
    panel: {
      pickModel: 'Pick a model',
      pickModelHint: 'Choose a trained version on the left, then add the images you want it to look at.',
      dropTitle: 'Drop images here',
      dropHint: 'Or click to choose. JPG, PNG, WEBP, BMP and TIFF.',
      addMore: 'Add more images',
      confidence: 'Confidence',
      run: 'Run',
      runAgain: 'Run again',
      stop: 'Stop',
      clear: 'Clear',
      progressRunning: 'Running {done} of {total}',
      progressIdle: '{done} of {total} done',
    },
    rejected: {
      unsupported: '{name} is not an image format this model can read.',
      tooLarge: '{name} is larger than 40 MB.',
    },
    result: {
      title: 'Result',
      idle: 'Results will appear here',
      pending: 'Not run yet',
      running: 'Running the model…',
      failedTitle: 'Prediction failed',
      nothing: 'Nothing found',
      nothingHint: 'The model found nothing it was at least {conf}% sure about. Lower the confidence to see more.',
      predictedClass: 'Predicted class',
      confident: '{value} confident',
      allClasses: 'All classes',
      found: '{count} found',
      image: 'Image',
      model: 'Model',
      took: 'Took',
      size: 'Size',
    },
  },
};

export default en;
